import React, { createContext, useContext, useEffect, useMemo, forwardRef } from 'react';
import kebabCase from 'lodash/kebabCase';

var EVENT = {
    PAGE_VIEW: 'SPAPageView',
    EVENT: 'GTMevent',
    ECOMMERCE: 'GAecommerce',
    ENHANCED_ECOMMERCE: 'enhancedEcommerce',
};
var EVENT_ACTION = {
    BUTTON_CLICK: 'button-click',
    CARD_CLICK: 'card-click',
    TAB_CLICK: 'tab-click',
    LINK_CLICK: 'link-click',
    FIELD_CLICK: 'field-click',
    ICON_CLICK: 'icon-click',
    FORM_COMPLETION: 'form-completion',
    CHECKBOX_SELECTED: 'checkbox-selected',
    CHECKBOX_DESELECTED: 'checkbox-deselected',
    SOCIAL_SHARE: 'social-share',
    PAY_TYPE_SELECTION: 'pay_type_selection',
    DISPLAYED: 'displayed',
    SAILOR_SELECTION: 'sailor-selection',
    SAILOR_DESELECTION: 'sailor-deselection',
    MEASURING_PURCHASES: 'measuring-purchases',
    ERROR: 'error',
    TITLE_CLICK: 'title-click',
    TILE_CLICK: 'tile-click',
};
var ENHANCED_ECOMMERCE_ACTION = {
    CLICK: 'measuring-promotion-clicks',
    VIEW: 'measuring-promotion-views',
};
var ECOMMERCE_ACTION = {
    PURCHASES: 'measuring-purchases',
};
var EVENT_NAME_PREFIX = {
    POP_UP: 'pop_up',
    FLYOUT: 'flyout',
};
var GoogleAnalyticsConstants = { EVENT: EVENT, EVENT_ACTION: EVENT_ACTION, ENHANCED_ECOMMERCE_ACTION: ENHANCED_ECOMMERCE_ACTION, ECOMMERCE_ACTION: ECOMMERCE_ACTION, EVENT_NAME_PREFIX: EVENT_NAME_PREFIX };

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var buildCategories = function (categories) { return categories.join('|'); };
var getKebabCaseName$1 = function (label) { return (label === null || label === void 0 ? void 0 : label.split('|').map(kebabCase).join('|')) || ''; };
var getGoogleAnalyticsEventContext = function (currentGoogleAnalyticsEventContext, googleAnalyticsTrackContext, categoryToLabel, isSubCategory, category, event, eventNamePrefix) { return (__assign(__assign({}, currentGoogleAnalyticsEventContext), { _track: googleAnalyticsTrackContext.track, event: function (eventAction, eventLabel, categories, extra) {
        if (categories === void 0) { categories = []; }
        if (extra === void 0) { extra = {}; }
        var customLabel = categoryToLabel ? buildCategories([eventLabel, category]) : eventLabel;
        var listCategories = categoryToLabel ? categories : __spreadArray([category], categories, true);
        var eventName = eventNamePrefix ? getKebabCaseName$1(eventNamePrefix.concat(' ', eventAction)) : eventAction;
        if (isSubCategory) {
            currentGoogleAnalyticsEventContext.event.call(this, eventAction, customLabel, listCategories, extra, eventNamePrefix);
        }
        else {
            this._track(__assign({ event: event, eventAction: eventAction, eventLabel: getKebabCaseName$1(customLabel), eventCategory: buildCategories(listCategories), event_name: eventName.replace('-', '_'), element_title: getKebabCaseName$1(customLabel), element_type: eventAction.substring(0, eventAction.indexOf('-')) }, extra));
        }
    } })); };

var getKebabCaseName = function (label) { return (label === null || label === void 0 ? void 0 : label.split('|').map(kebabCase).join('|')) || ''; };
var GoogleAnalyticsEventContext = createContext({
    _track: function () { },
    event: function (eventAction, eventLabel, categories, extra) {
        if (categories === void 0) { categories = []; }
        if (extra === void 0) { extra = {}; }
        this._track(__assign({ event: GoogleAnalyticsConstants.EVENT.EVENT, eventAction: eventAction, eventLabel: getKebabCaseName(eventLabel), eventCategory: buildCategories(categories), event_name: eventAction.replace('-', '_'), element_title: getKebabCaseName(eventLabel), element_type: eventAction.substring(0, eventAction.indexOf('-')) }, extra));
    },
    buttonClick: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.BUTTON_CLICK, label);
    },
    cardClick: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.CARD_CLICK, label);
    },
    tabClick: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.TAB_CLICK, label);
    },
    linkClick: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.LINK_CLICK, label);
    },
    fieldClick: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.FIELD_CLICK, label);
    },
    iconClick: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.ICON_CLICK, label);
    },
    formCompletion: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.FORM_COMPLETION, label);
    },
    checkboxSelected: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.CHECKBOX_SELECTED, label);
    },
    checkboxDeselected: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.CHECKBOX_DESELECTED, label);
    },
    sailorSelected: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.SAILOR_SELECTION, label);
    },
    sailorDeselected: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.SAILOR_DESELECTION, label);
    },
    checkbox: function (label, isSelected) {
        if (isSelected === void 0) { isSelected = true; }
        if (isSelected) {
            this.checkboxSelected(label);
        }
        else {
            this.checkboxDeselected(label);
        }
    },
    socialShare: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.SOCIAL_SHARE, label);
    },
    displayed: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.DISPLAYED, label);
    },
    error: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.ERROR, label);
    },
    titleClick: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.TITLE_CLICK, label);
    },
    tileClick: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.TILE_CLICK, label);
    },
    payTypeSelect: function (label) {
        this.event(GoogleAnalyticsConstants.EVENT_ACTION.PAY_TYPE_SELECTION, label);
    },
});
GoogleAnalyticsEventContext.displayName = 'GoogleAnalyticsEventContext';

var useGoogleAnalyticsEventContext = function () { return useContext(GoogleAnalyticsEventContext); };

var GoogleAnalyticsDisplayedEvent = function (_a) {
    var eventAction = _a.eventAction, eventLabel = _a.eventLabel;
    var googleAnalyticsEventContext = useGoogleAnalyticsEventContext();
    useEffect(function () {
        if (eventAction) {
            switch (eventAction) {
                case GoogleAnalyticsConstants.EVENT_ACTION.ERROR:
                    googleAnalyticsEventContext.error(eventLabel || '');
                    break;
                case GoogleAnalyticsConstants.EVENT_ACTION.DISPLAYED:
                    googleAnalyticsEventContext.displayed(eventLabel || '');
                    break;
            }
        }
    }, []);
    return null;
};

var GoogleAnalyticsEcommerceContext = createContext({
    _track: function (event) { },
    event: function (eventAction, eventLabel, categories, extra) {
        if (categories === void 0) { categories = []; }
        if (extra === void 0) { extra = {}; }
        this._track(__assign({ event: GoogleAnalyticsConstants.EVENT.ECOMMERCE, eventAction: eventAction, eventLabel: eventLabel, categories: buildCategories(categories) }, extra));
    },
    purchases: function (eventLabel, extra) {
        if (extra === void 0) { extra = {}; }
        this.event(GoogleAnalyticsConstants.ECOMMERCE_ACTION.PURCHASES, eventLabel, [], extra);
    },
});
GoogleAnalyticsEcommerceContext.displayName = 'GoogleAnalyticsEcommerceContext';

var useGoogleAnalyticsEcommerceContext = function () { return useContext(GoogleAnalyticsEcommerceContext); };

var GoogleAnalyticsTrackContext = createContext({
    track: function (event) { },
});
GoogleAnalyticsTrackContext.displayName = 'GoogleAnalyticsTrackContext';

var useGoogleAnalyticsTrackContext = function () { return useContext(GoogleAnalyticsTrackContext); };

var GoogleAnalyticsEcommerceContextWrapper = function (_a) {
    var children = _a.children, category = _a.category, _b = _a.isSubCategory, isSubCategory = _b === void 0 ? false : _b, _c = _a.categoryToLabel, categoryToLabel = _c === void 0 ? false : _c;
    var currentGoogleAnalyticsEcommerceContext = useGoogleAnalyticsEcommerceContext();
    var googleAnalyticsTrackContext = useGoogleAnalyticsTrackContext();
    var googleAnalyticsEcommerceContext = useMemo(function () {
        return getGoogleAnalyticsEventContext(currentGoogleAnalyticsEcommerceContext, googleAnalyticsTrackContext, categoryToLabel, isSubCategory, category, GoogleAnalyticsConstants.EVENT.ECOMMERCE);
    }, [currentGoogleAnalyticsEcommerceContext, googleAnalyticsTrackContext, category, isSubCategory, categoryToLabel]);
    return (React.createElement(GoogleAnalyticsEcommerceContext.Provider, { value: googleAnalyticsEcommerceContext }, children));
};

var GoogleAnalyticsEnhancedEcommerceContext = createContext({
    _track: function (event) { },
    event: function (eventAction, eventLabel, categories, extra) {
        if (categories === void 0) { categories = []; }
        if (extra === void 0) { extra = {}; }
        this._track(__assign({ event: GoogleAnalyticsConstants.EVENT.ENHANCED_ECOMMERCE, eventAction: eventAction, eventLabel: eventLabel, categories: buildCategories(categories) }, extra));
    },
    measuringPromotionsClick: function (label, extra) {
        if (extra === void 0) { extra = {}; }
        this.event(GoogleAnalyticsConstants.ENHANCED_ECOMMERCE_ACTION.CLICK, label, [], extra);
    },
    measuringPromotionsView: function (label, extra) {
        if (extra === void 0) { extra = {}; }
        this.event(GoogleAnalyticsConstants.ENHANCED_ECOMMERCE_ACTION.VIEW, label, [], extra);
    },
});
GoogleAnalyticsEnhancedEcommerceContext.displayName = 'GoogleAnalyticsEnhancedEcommerceContext';

var useGoogleAnalyticsEnhancedEcommerceContext = function () { return useContext(GoogleAnalyticsEnhancedEcommerceContext); };

var GoogleAnalyticsEnhancedEcommerceContextWrapper = function (_a) {
    var children = _a.children, category = _a.category, _b = _a.isSubCategory, isSubCategory = _b === void 0 ? false : _b, _c = _a.categoryToLabel, categoryToLabel = _c === void 0 ? false : _c;
    var currentGoogleAnalyticsEnhancedEcommerceContext = useGoogleAnalyticsEnhancedEcommerceContext();
    var googleAnalyticsTrackContext = useGoogleAnalyticsTrackContext();
    var googleAnalyticsEnhancedEcommerceContext = useMemo(function () {
        return getGoogleAnalyticsEventContext(currentGoogleAnalyticsEnhancedEcommerceContext, googleAnalyticsTrackContext, categoryToLabel, isSubCategory, category, GoogleAnalyticsConstants.EVENT.ENHANCED_ECOMMERCE);
    }, [
        currentGoogleAnalyticsEnhancedEcommerceContext,
        googleAnalyticsTrackContext,
        category,
        isSubCategory,
        categoryToLabel,
    ]);
    return (React.createElement(GoogleAnalyticsEnhancedEcommerceContext.Provider, { value: googleAnalyticsEnhancedEcommerceContext }, children));
};

var GoogleAnalyticsEnhancedEcommerceView = function (_a) {
    var eventAction = _a.eventAction, eventLabel = _a.eventLabel, _b = _a.extraData, extraData = _b === void 0 ? {} : _b;
    var googleAnalyticsEventContext = useGoogleAnalyticsEnhancedEcommerceContext();
    useEffect(function () {
        if (eventAction) {
            switch (eventAction) {
                case GoogleAnalyticsConstants.ENHANCED_ECOMMERCE_ACTION.VIEW:
                    googleAnalyticsEventContext.measuringPromotionsView(eventLabel, extraData);
                    break;
            }
        }
    }, []);
    return null;
};

var GoogleAnalyticsEventContextWrapper = function (_a) {
    var children = _a.children, category = _a.category, _b = _a.isSubCategory, isSubCategory = _b === void 0 ? false : _b, _c = _a.categoryToLabel, categoryToLabel = _c === void 0 ? false : _c, _d = _a.eventNamePrefix, eventNamePrefix = _d === void 0 ? '' : _d;
    var currentGoogleAnalyticsEventContext = useGoogleAnalyticsEventContext();
    var googleAnalyticsTrackContext = useGoogleAnalyticsTrackContext();
    var googleAnalyticsEventContext = useMemo(function () {
        return getGoogleAnalyticsEventContext(currentGoogleAnalyticsEventContext, googleAnalyticsTrackContext, categoryToLabel, isSubCategory, category, GoogleAnalyticsConstants.EVENT.EVENT, eventNamePrefix);
    }, [
        currentGoogleAnalyticsEventContext,
        googleAnalyticsTrackContext,
        category,
        isSubCategory,
        categoryToLabel,
        eventNamePrefix,
    ]);
    return (React.createElement(GoogleAnalyticsEventContext.Provider, { value: googleAnalyticsEventContext }, children));
};

var withGoogleAnalyticsClickEvent = function (Target, actionMatcher, labelMatcher) {
    if (actionMatcher === void 0) { actionMatcher = function (props) { return GoogleAnalyticsConstants.EVENT_ACTION.BUTTON_CLICK; }; }
    if (labelMatcher === void 0) { labelMatcher = function (props) {
        return typeof props.children === 'string' ? props.children : null;
    }; }
    return forwardRef(function (_a, ref) {
        var onClick = _a.onClick, trackAction = _a.trackAction, trackLabel = _a.trackLabel, props = __rest(_a, ["onClick", "trackAction", "trackLabel"]);
        var googleAnalyticsEventContext = useGoogleAnalyticsEventContext();
        var propsWithDefaults = __assign({}, props);
        var eventAction = trackAction || actionMatcher(propsWithDefaults);
        var eventLabel = trackLabel || labelMatcher(propsWithDefaults);
        var handleClick = function (e) {
            if (eventLabel) {
                switch (eventAction) {
                    case GoogleAnalyticsConstants.EVENT_ACTION.BUTTON_CLICK:
                        googleAnalyticsEventContext.buttonClick(eventLabel);
                        break;
                    case GoogleAnalyticsConstants.EVENT_ACTION.CARD_CLICK:
                        googleAnalyticsEventContext.cardClick(eventLabel);
                        break;
                    case GoogleAnalyticsConstants.EVENT_ACTION.TAB_CLICK:
                        googleAnalyticsEventContext.tabClick(eventLabel);
                        break;
                    case GoogleAnalyticsConstants.EVENT_ACTION.LINK_CLICK:
                        googleAnalyticsEventContext.linkClick(eventLabel);
                        break;
                    case GoogleAnalyticsConstants.EVENT_ACTION.FIELD_CLICK:
                        googleAnalyticsEventContext.fieldClick(eventLabel);
                        break;
                    case GoogleAnalyticsConstants.EVENT_ACTION.ICON_CLICK:
                        googleAnalyticsEventContext.iconClick(eventLabel);
                        break;
                    case GoogleAnalyticsConstants.EVENT_ACTION.CHECKBOX_SELECTED:
                        googleAnalyticsEventContext.checkboxSelected(eventLabel);
                        break;
                    case GoogleAnalyticsConstants.EVENT_ACTION.SAILOR_SELECTION:
                        googleAnalyticsEventContext.sailorSelected(eventLabel);
                        break;
                    case GoogleAnalyticsConstants.EVENT_ACTION.SAILOR_DESELECTION:
                        googleAnalyticsEventContext.sailorDeselected(eventLabel);
                        break;
                    case GoogleAnalyticsConstants.EVENT_ACTION.CHECKBOX_DESELECTED:
                        googleAnalyticsEventContext.checkboxDeselected(eventLabel);
                        break;
                    case GoogleAnalyticsConstants.EVENT_ACTION.TITLE_CLICK:
                        googleAnalyticsEventContext.titleClick(eventLabel);
                        break;
                    case GoogleAnalyticsConstants.EVENT_ACTION.SOCIAL_SHARE:
                        googleAnalyticsEventContext.socialShare(eventLabel);
                        break;
                    case GoogleAnalyticsConstants.EVENT_ACTION.TILE_CLICK:
                        googleAnalyticsEventContext.tileClick(eventLabel);
                        break;
                }
            }
            if (onClick) {
                onClick(e);
            }
        };
        return React.createElement(Target, __assign({ ref: ref }, props, { onClick: handleClick }));
    });
};

var withGoogleAnalyticsEcommerceContext = function (Target, category, isSubCategory, categoryToLabel) {
    if (isSubCategory === void 0) { isSubCategory = false; }
    if (categoryToLabel === void 0) { categoryToLabel = false; }
    return function (props) { return (React.createElement(GoogleAnalyticsEcommerceContextWrapper, { isSubCategory: isSubCategory, category: category, categoryToLabel: categoryToLabel },
        React.createElement(Target, __assign({}, props)))); };
};

var withGoogleAnalyticsEnhancedEcommerceClickEvent = function (Target, actionMatcher, labelMatcher) {
    if (actionMatcher === void 0) { actionMatcher = function (props) { return GoogleAnalyticsConstants.EVENT_ACTION.BUTTON_CLICK; }; }
    if (labelMatcher === void 0) { labelMatcher = function (props) {
        return typeof props.children === 'string' ? props.children : null;
    }; }
    return function (_a) {
        var onClick = _a.onClick, trackAction = _a.trackAction, trackLabel = _a.trackLabel, extraData = _a.extraData, props = __rest(_a, ["onClick", "trackAction", "trackLabel", "extraData"]);
        var googleAnalyticsEventContext = useGoogleAnalyticsEnhancedEcommerceContext();
        var propsWithDefaults = __assign({}, props);
        var eventAction = trackAction || actionMatcher(propsWithDefaults);
        var eventLabel = trackLabel || labelMatcher(propsWithDefaults);
        var handleClick = function (e) {
            if (eventLabel) {
                switch (eventAction) {
                    case GoogleAnalyticsConstants.ENHANCED_ECOMMERCE_ACTION.CLICK:
                        googleAnalyticsEventContext.measuringPromotionsClick(eventLabel, extraData);
                        break;
                }
            }
            if (onClick) {
                onClick(e);
            }
        };
        return React.createElement(Target, __assign({}, props, { onClick: handleClick }));
    };
};

var withGoogleAnalyticsEventContext = function (Target, category, isSubCategory, categoryToLabel, eventNamePrefix) {
    if (isSubCategory === void 0) { isSubCategory = false; }
    if (categoryToLabel === void 0) { categoryToLabel = false; }
    if (eventNamePrefix === void 0) { eventNamePrefix = ''; }
    return function (props) { return (React.createElement(GoogleAnalyticsEventContextWrapper, { isSubCategory: isSubCategory, category: category, categoryToLabel: categoryToLabel, eventNamePrefix: eventNamePrefix },
        React.createElement(Target, __assign({}, props)))); };
};

export { GoogleAnalyticsConstants, GoogleAnalyticsDisplayedEvent, GoogleAnalyticsEcommerceContextWrapper, GoogleAnalyticsEnhancedEcommerceContextWrapper, GoogleAnalyticsEnhancedEcommerceView, GoogleAnalyticsEventContext, GoogleAnalyticsEventContextWrapper, GoogleAnalyticsTrackContext, useGoogleAnalyticsEcommerceContext, useGoogleAnalyticsEventContext, useGoogleAnalyticsTrackContext, withGoogleAnalyticsClickEvent, withGoogleAnalyticsEcommerceContext, withGoogleAnalyticsEnhancedEcommerceClickEvent, withGoogleAnalyticsEventContext };
//# sourceMappingURL=index.js.map
